/**
 * React Query hooks for programme actions.
 * React Query hooks for the corresponding entity.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "common/queryKeys";
import { ProgrammeActions } from "model/programmeActionsTypes";
import { useMemo } from "react";

// Re-export types for convenience
export type { ProgrammeActions };

/**
 * API for fetching programme actions
 */
async function fetchProgrammeActions(): Promise<ProgrammeActions[]> {
  const res = await fetch(
    `${import.meta.env.VITE_API_ENDPOINT}/v2/programmesActions`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
        "Content-Type": "application/json",
      },
    }
  );
  // Return empty array if API returns error (e.g., 404, 500)
  if (!res.ok) {
    console.warn(`Failed to fetch programme actions: ${res.status}`);
    return [];
  }
  return res.json() as Promise<unknown>;
}

/**
 * Fetch all programme actions
 */
export function useProgrammeActions(enabled = true) {
  return useQuery({
    queryKey: queryKeys.programmeActions.all,
    queryFn: fetchProgrammeActions,
    enabled,
  });
}

/**
 * React Query hook for fetching a single programme action by ID.
 * Uses useProgrammeActions() internally to get proper React Query subscriptions.
 */
export function useProgrammeActionById(programmeId: string | undefined): {
  data: ProgrammeActions | undefined;
  isLoading: boolean;
} {
  const { data: allProgrammes, isLoading } = useProgrammeActions();

  const programme = useMemo(() => {
    if (!programmeId || !allProgrammes) return undefined;
    return allProgrammes.find((p) => p.guid === programmeId);
  }, [programmeId, allProgrammes]);

  return {
    data: programme,
    isLoading,
  };
}

/**
 * Helper function to reset programme actions query cache
 */
export function resetProgrammeActionsQueryCache(
  queryClient: ReturnType<typeof useQueryClient>
): void {
  queryClient.removeQueries({ queryKey: queryKeys.programmeActions.all });
}

/**
 * Hook wrapper for resetProgrammeActionsQueryCache
 */
export function useResetProgrammeActionsQueryCache() {
  const queryClient = useQueryClient();
  return () => resetProgrammeActionsQueryCache(queryClient);
}

/**
 * Fetch programme actions directly (not a hook, for use in services).
 */
export async function fetchProgrammeActionsQuery(): Promise<
  ProgrammeActions[]
> {
  return fetchProgrammeActions();
}

/**
 * Derive the farmer's programme type from their programme memberships.
 * Returns "DAIRY" if any programme is dairy, "BEEF" if any is beef, or null if no programmes.
 */
export function useFarmerProgrammeType(): {
  programmeType: "BEEF" | "DAIRY" | null;
  isLoading: boolean;
} {
  const { data: programmes, isLoading } = useProgrammeActions();

  const programmeType = useMemo(() => {
    if (!programmes || programmes.length === 0) return null;
    const hasDairy = programmes.some((p) => p.type === "DAIRY");
    if (hasDairy) return "DAIRY" as const;
    return "BEEF" as const;
  }, [programmes]);

  return { programmeType, isLoading };
}
